<template>
  <div
    :class="tagClasses"
    :aria-label="props.ariaLabel"
    role="status"
    data-cy="ds-tag"
  >
    <DSIcon
      v-if="props.prefixIcon"
      :icon="props.prefixIcon"
      size="sm"
    />

    <span
      :class="DS_TAG_CLASSES.Text"
      data-cy="ds-tag__text"
    >
      {{ props.text }}
    </span>

    <DSBadge
      v-if="props.badgeText"
      :text="props.badgeText"
      :disabled="props.disabled"
      state="neutral"
      data-cy="ds-tag__badge"
    />

    <DSButton
      v-if="props.removable"
      :class="DS_TAG_CLASSES.RemoveButton"
      variant="ghost"
      size="xs"
      :aria-label="removeButtonAriaLabel"
      :disabled="props.disabled"
      type="button"
      data-cy="ds-tag__remove-button"
      @click="handleRemove"
    >
      <DSIcon
        icon="cross-small"
        size="sm"
        aria-hidden="true"
        data-cy="ds-tag__action-icon"
      />
    </DSButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { DSTagProps } from "@/src/design_system/data_display/tag/types";
import { DS_TAG_CLASSES, DS_TAG_COLOR_PALETTE_TO_CLASS_MAP } from "@/src/design_system/data_display/tag/constants";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSBadge from "@/src/design_system/data_display/badge/components/DSBadge.vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { useStateClasses } from "@/src/design_system/shared/composables/useStateClasses";

const props = withDefaults(defineProps<DSTagProps>(), {
  colorPalette: undefined,
  disabled: false,
  prefixIcon: undefined,
  removable: false,
  badgeText: undefined,
  ariaLabel: undefined,
});

const emit = defineEmits<{
  (e: "remove"): void;
}>();

const { stateClasses } = useStateClasses(props);

const tagClasses = computed(() => {
  return [DS_TAG_CLASSES.Tag, tagColorPaletteClass.value, stateClasses.value];
});

const tagColorPaletteClass = computed(() => {
  if (props.colorPalette) return DS_TAG_COLOR_PALETTE_TO_CLASS_MAP[props.colorPalette];

  return null;
});

const removeButtonAriaLabel = computed(() => {
  return window.I18n.designSystem.tag.ariaLabelRemove.replace("%{text}", () => props.text);
});

const handleRemove = (e: Event) => {
  e.stopPropagation();
  if (!props.disabled) {
    emit("remove");
  }
};
</script>
